/**
 * @file src/shell/AnimBar.js
 * Thanh điều khiển animation của bước. HAI CHẾ ĐỘ, chọn bằng loại của clip:
 *
 *   'play'   (clip loại 1 — scene1_1_ten): nút tròn phát/tạm dừng + rãnh tiến độ +
 *            đồng hồ. Kiểu Mozaik thường.
 *   'stages' (clip loại 2 — scene1_2_ten): animation theo TIẾN TRÌNH. Không tự chạy;
 *            rãnh chia thành các giai đoạn có vạch, kéo qua lại giữa chúng, nhả tay thì
 *            bám vào vạch gần nhất. Hai nút mũi tên đi lùi/tiến một giai đoạn.
 *
 * Vì sao nằm ở shell mà không phải trong tiết: nó là một dải neo theo khung 3D, phải
 * nằm đúng trên thanh bước và co theo bảng trắng như .ol-toolrail. Tiết dựng DOM rồi
 * tự chèn vào .ol-stage thì mỗi tiết lại đặt một kiểu, và không nghe được chế độ gọn.
 *
 * Thanh KHÔNG tự chạy đồng hồ. Tiết gọi setProgress() mỗi frame từ thời gian thật của
 * AnimationAction — nguồn duy nhất là mixer, nên kéo rãnh và tua đều khớp tuyệt đối,
 * không có chuyện thanh trôi lệch so với mô hình.
 */
import { icon } from './icons.js';

/**
 * @typedef {Object} AnimBarConfig
 * @property {number} duration
 * @property {boolean} [playing]
 * @property {'play'|'stages'} [mode]
 * @property {number[]} [stages] mốc thời gian (giây) của từng giai đoạn, chế độ 'stages'
 */

/**
 * @typedef {Object} AnimBarHandle
 * @property {(o: AnimBarConfig | null) => void} show  null = ẩn
 * @property {(playing: boolean) => void} setPlaying
 * @property {(t: number) => void} setProgress  giây, tính từ đầu clip
 * @property {() => void} remove
 */

/**
 * @param {Object} o
 * @param {HTMLElement} o.stage
 * @param {(t: string) => string} o.t
 * @param {() => void} o.onToggle          bấm nút tròn (chế độ 'play')
 * @param {(t: number) => void} o.onSeek   kéo/bấm vào rãnh, giây
 * @returns {AnimBarHandle}
 */
export function createAnimBar(o) {
  const el = document.createElement('div');
  el.className = 'ol-animbar';
  el.hidden = true;
  el.innerHTML = `
    <button class="ol-animbar__btn ol-animbar__btn--play" type="button">
      <span class="ol-icon ol-animbar__ico"></span>
    </button>
    <button class="ol-animbar__btn ol-animbar__btn--prev" type="button">
      <span class="ol-icon">${icon('stagePrev')}</span>
    </button>
    <button class="ol-animbar__btn ol-animbar__btn--next" type="button">
      <span class="ol-icon">${icon('stageNext')}</span>
    </button>
    <div class="ol-animbar__track" role="slider" tabindex="0"
         aria-valuemin="0" aria-valuemax="0" aria-valuenow="0">
      <div class="ol-animbar__fill"></div>
      <div class="ol-animbar__ticks"></div>
      <div class="ol-animbar__knob"></div>
    </div>
    <span class="ol-animbar__time">0:00</span>`;
  o.stage.appendChild(el);

  const q = (/** @type {string} */ sel) => /** @type {HTMLElement} */ (el.querySelector(sel));
  const playBtn = /** @type {HTMLButtonElement} */ (q('.ol-animbar__btn--play'));
  const prevBtn = /** @type {HTMLButtonElement} */ (q('.ol-animbar__btn--prev'));
  const nextBtn = /** @type {HTMLButtonElement} */ (q('.ol-animbar__btn--next'));
  const ico = q('.ol-animbar__ico');
  const track = q('.ol-animbar__track');
  const fill = q('.ol-animbar__fill');
  const ticks = q('.ol-animbar__ticks');
  const label = q('.ol-animbar__time');

  let duration = 0;
  let playing = false;
  /** @type {'play'|'stages'} */
  let mode = 'play';
  /** @type {number[]} */
  let stages = [];
  let now = 0;

  /** @param {number} s */
  const mmss = (s) => {
    const v = Math.max(0, Math.round(s));
    return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;
  };

  /** Chỉ số giai đoạn gần thời điểm t nhất. @param {number} t */
  const stageAt = (t) => {
    let best = 0;
    for (let i = 1; i < stages.length; i++) {
      if (Math.abs((stages[i] ?? 0) - t) < Math.abs((stages[best] ?? 0) - t)) best = i;
    }
    return best;
  };

  /** Mốc thời gian của giai đoạn thứ i, kẹp trong biên. @param {number} i */
  const stageTime = (i) => stages[Math.min(stages.length - 1, Math.max(0, i))] ?? 0;

  const paintBtn = () => {
    ico.innerHTML = icon(playing ? 'pause' : 'play');
    const key = playing ? 'anim.pause' : 'anim.play';
    playBtn.setAttribute('aria-label', o.t(key));
    playBtn.title = o.t(key);
    el.classList.toggle('is-playing', playing);
  };

  const paintLabel = () => {
    // Chế độ 'play' hiện THỜI GIAN CÒN LẠI, không phải thời gian đã trôi: lúc chưa bấm
    // thanh đã cho biết clip dài bao nhiêu (0:17), bấm rồi thì đếm ngược về 0:00. Đếm
    // xuôi thì trước khi bấm chỉ thấy 0:00 — không nói lên điều gì.
    if (mode === 'stages') label.textContent = `${stageAt(now) + 1}/${stages.length}`;
    else label.textContent = mmss(Math.max(0, duration - now));
  };

  /** @param {PointerEvent} ev @returns {number} giây tại vị trí con trỏ */
  const timeAt = (ev) => {
    const r = track.getBoundingClientRect();
    const k = Math.min(1, Math.max(0, (ev.clientX - r.left) / Math.max(1, r.width)));
    return k * duration;
  };

  playBtn.addEventListener('click', () => o.onToggle());
  // Đi một giai đoạn: tính từ giai đoạn ĐANG ở, không cộng dồn thời gian — các giai đoạn
  // thường không cách đều nhau.
  prevBtn.addEventListener('click', () => {
    if (stages.length) o.onSeek(stageTime(stageAt(now) - 1));
  });
  nextBtn.addEventListener('click', () => {
    if (stages.length) o.onSeek(stageTime(stageAt(now) + 1));
  });

  track.addEventListener('pointerdown', (ev) => {
    track.setPointerCapture(ev.pointerId);
    o.onSeek(timeAt(ev));
  });
  track.addEventListener('pointermove', (ev) => {
    // buttons=1 tức đang GIỮ chuột — không có phép thử này thì rê chuột ngang qua rãnh
    // lúc không bấm cũng tua, mô hình giật liên tục khi con trỏ chỉ đi ngang.
    if (ev.buttons & 1) o.onSeek(timeAt(ev));
  });
  // Nhả tay ở chế độ giai đoạn thì BÁM vào vạch gần nhất: kéo tự do cho thấy chuyển động
  // liên tục, nhưng dừng lại luôn ở một giai đoạn trọn vẹn chứ không lửng lơ giữa hai.
  track.addEventListener('pointerup', () => {
    if (mode === 'stages' && stages.length) o.onSeek(stageTime(stageAt(now)));
  });
  track.addEventListener('keydown', (ev) => {
    if (duration <= 0) return;
    const byStage = mode === 'stages' && stages.length > 0;
    if (ev.key === 'ArrowLeft') {
      o.onSeek(byStage ? stageTime(stageAt(now) - 1) : Math.max(0, now - duration / 20));
    } else if (ev.key === 'ArrowRight') {
      o.onSeek(byStage ? stageTime(stageAt(now) + 1) : Math.min(duration, now + duration / 20));
    } else if ((ev.key === ' ' || ev.key === 'Enter') && mode === 'play') {
      o.onToggle();
    } else return;
    ev.preventDefault();
  });

  return {
    show(cfg) {
      if (!cfg) {
        el.hidden = true;
        return;
      }
      duration = Math.max(0, cfg.duration);
      playing = Boolean(cfg.playing);
      mode = cfg.mode ?? 'play';
      stages = mode === 'stages' ? (cfg.stages ?? []).filter((v) => v <= duration + 1e-6) : [];
      el.hidden = false;
      el.classList.toggle('is-stages', mode === 'stages');
      track.setAttribute('aria-valuemax', String(Math.round(duration)));
      ticks.innerHTML = stages
        .map((v) => {
          const k = duration > 0 ? (v / duration) * 100 : 0;
          return `<i class="ol-animbar__tick" style="left:${k}%"></i>`;
        })
        .join('');
      paintBtn();
      this.setProgress(0);
    },
    setPlaying(v) {
      playing = v;
      paintBtn();
    },
    setProgress(t) {
      now = t;
      const k = duration > 0 ? Math.min(1, Math.max(0, t / duration)) : 0;
      fill.style.width = `${k * 100}%`;
      el.style.setProperty('--ol-anim-k', String(k));
      track.setAttribute('aria-valuenow', String(Math.round(t)));
      paintLabel();
    },
    remove() {
      el.remove();
    },
  };
}
